import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../ui/dialog";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Loader2 } from "lucide-react";
import { mcPost, money, pct, ALERT_STYLE } from "./api";
import { CostingLines } from "./CostingLines";
import { PricePanel, BasePricePanel } from "./PricePanel";

const Metric = ({ l, v, testid, cls = "" }) => (
  <div className={`rounded border border-stone-200 px-2 py-1 ${cls}`}><p className="text-[10px] uppercase text-stone-500">{l}</p><p className="text-sm font-bold" data-testid={testid}>{v}</p></div>
);

function WastageBlock({ w, cur }) {
  if (!w) return null;
  return (
    <div className="rounded border border-stone-200 p-2 text-xs" data-testid="wastage-block">
      <p className="font-semibold">Actual preparation & wastage ({w.from} → {w.to}) {w.units?.length ? `· ${w.units.join(", ")}` : ""}</p>
      <div className="grid grid-cols-5 gap-2 mt-1">
        <span>Prepared: <b data-testid="w-prepared">{w.prepared_qty}</b></span><span>Sold (POS): <b data-testid="w-sold">{w.sold_qty_pos}</b></span>
        <span>Wasted: <b data-testid="w-wasted">{w.wasted_qty}</b></span><span>Remaining: <b data-testid="w-remaining">{w.remaining_qty ?? "n/a"}</b></span>
        <span>Wastage cost: <b data-testid="w-cost">{money(w.wastage_cost, cur)}</b></span>
      </div>
      <p className="text-[10px] text-stone-500 mt-1">{w.note}</p>
    </div>
  );
}

export const CostingEditDialog = ({ open, onClose, token, center, item, meta, channel, onSaved }) => {
  const [d, setD] = useState(null);
  const [m, setM] = useState(null);
  const [busy, setBusy] = useState(false);
  const [editMap, setEditMap] = useState(false);
  const [rateEdit, setRateEdit] = useState(false);
  const [baseOpen, setBaseOpen] = useState(false);
  const [centreOpen, setCentreOpen] = useState(false);
  const nav = useNavigate();
  const perms = meta.permissions || {};

  const load = useCallback(async (keep) => {
    try {
      const r = await mcPost("item", { token, center, menu_item_id: item.menu_item_id, channel });
      setD(r);
      if (!keep) setM({ recipe_key: r.map.recipe_key, yield_servings: r.map.yield_servings, lines: r.lines,
        packing_dine_in: r.map.packing_dine_in || [], packing_takeaway: r.map.packing_takeaway || [] });
    } catch (e) { toast.error(e.message); }
  }, [token, center, item, channel]);
  useEffect(() => { if (open) { setD(null); load(false); } }, [open, load]);

  const save = async () => {
    setBusy(true);
    try {
      await mcPost("map/save", { token, menu_item_id: item.menu_item_id, recipe_key: m.recipe_key || null,
        yield_servings: m.yield_servings ? parseFloat(m.yield_servings) : null, lines: m.lines,
        packing_dine_in: m.packing_dine_in, packing_takeaway: m.packing_takeaway, reason: "Costing Map confirmed" });
      toast.success("Costing Map saved — shared across all centres");
      setEditMap(false); await load(false); onSaved();
    } catch (e) { toast.error(e.message); } finally { setBusy(false); }
  };
  const changeRecipe = async (key) => {
    if (!key) return setM({ ...m, recipe_key: "", lines: [] });
    try { const r = await mcPost("suggest-lines", { token, recipe_key: key }); setM({ ...m, recipe_key: key, lines: r.lines }); }
    catch (e) { toast.error(e.message); }
  };
  const saveRate = async (l, rate, scope) => {
    const reason = window.prompt(`Reason for approved ${scope === "CENTRE" ? center : "default"} rate of ${l.item_name}:`, "Approved rate — no recent purchase");
    if (!reason) return;
    try { await mcPost("rate/save", { token, center, item_code: l.item_code, rate, scope, reason }); toast.success("Rate saved — recalculated"); await load(editMap); onSaved(); }
    catch (e) { toast.error(e.message); }
  };
  const mapEditable = perms.can_edit_map && editMap;
  const cur = d?.currency || "₹";
  const pkKey = channel === "takeaway" ? "packing_takeaway" : "packing_dine_in";

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-5xl max-h-[92vh] overflow-y-auto" data-testid="costing-edit-dialog">
        <DialogHeader><DialogTitle data-testid="costing-dialog-title">Menu Item — Recipe & Price Details · {item?.name} · {center}</DialogTitle>
          {d && <span className={`w-fit rounded px-2 py-0.5 text-xs ${d.cost_status === "COMPLETE" ? "bg-emerald-100 text-emerald-800" : "bg-red-100 text-red-800"}`} data-testid="cost-status-badge">{d.cost_status === "COMPLETE" ? "Cost Complete" : "Cost Incomplete"}</span>}</DialogHeader>
        {!d || !m ? <div className="py-10 flex justify-center"><Loader2 className="animate-spin" /></div> : (
          <div className="space-y-3">
            <div className="grid grid-cols-3 md:grid-cols-7 gap-2">
              <Metric l="Recipe cost / serving" v={money(d.recipe_cost, cur)} testid="detail-recipe-cost" />
              <Metric l={`Packing (${channel.replace("_", "-")})`} v={money(d.packing_cost, cur)} testid="detail-packing-cost" />
              <Metric l="Total cost / serving" v={money(d.total_cost, cur)} testid="detail-total-cost" />
              <Metric l="Price excl GST" v={money(d.price_excl, cur)} testid="detail-price-excl-metric" />
              <Metric l="Food cost %" v={pct(d.food_cost_pct)} testid="detail-food-cost-pct" cls={ALERT_STYLE[d.alert]} />
              <Metric l="Gross margin %" v={pct(d.gross_margin_pct)} testid="detail-gm-pct" />
              <Metric l={`Suggested @ ${d.settings.target_food_cost_pct}% (excl GST)`} v={perms.can_view_suggested ? money(d.suggested_excl, cur) : "—"} testid="detail-suggested-excl" />
            </div>
            {d.missing?.length > 0 && <div className="rounded bg-stone-100 p-2 text-[11px] text-stone-700" data-testid="detail-missing"><b>Data Missing:</b> {d.missing.join(" · ")}</div>}
            {d.causes?.length > 0 && <div className="rounded bg-amber-50 p-2 text-[11px] text-amber-900" data-testid="detail-causes"><b>Likely cause:</b> {d.causes.join(" · ")}</div>}
            {d.stale && <div className="rounded bg-orange-50 p-2 text-[11px] text-orange-800" data-testid="detail-stale">Recipe Master ingredients changed since this map was confirmed — please review and save.</div>}

            <div className="rounded border border-stone-200 p-3 space-y-2" data-testid="costing-map">
              <div className="flex flex-wrap items-end gap-3">
                <div className="flex-1 min-w-[220px]">
                  <p className="text-[10px] uppercase text-stone-500">Recipe (Recipe Master) · {d.recipe_status}</p>
                  <select className="h-8 w-full rounded border border-stone-300 px-1 text-xs" value={m.recipe_key || ""} disabled={!mapEditable}
                    onChange={(e) => changeRecipe(e.target.value)} data-testid="recipe-select">
                    <option value="">— no recipe —</option>
                    {meta.recipes.map((r) => <option key={r.key} value={r.key}>{r.name} ({r.category})</option>)}
                  </select>
                </div>
                <div className="w-40">
                  <p className="text-[10px] uppercase text-stone-500">Recipe yield (servings per batch)</p>
                  <Input className="h-8" type="number" step="0.1" disabled={!mapEditable} value={m.yield_servings ?? ""}
                    onChange={(e) => setM({ ...m, yield_servings: e.target.value })} data-testid="yield-input" />
                </div>
                <p className="text-[11px] text-stone-500">Batch cost {money(d.recipe_batch_cost, cur)} · rates: centre {d.rates_window_days}-day weighted average</p>
              </div>
              <CostingLines lines={m.lines} setLines={(lines) => setM({ ...m, lines })} items={meta.raw_items} editable={mapEditable} cur={cur} prefix="ing" rateEdit={rateEdit} onSaveRate={perms.can_edit_map ? saveRate : null} />
              <p className="text-[10px] uppercase text-stone-500 pt-2">Packing per serving — {channel === "takeaway" ? "Takeaway / pickup" : "Dine-in"}</p>
              <CostingLines packing lines={m[pkKey]} setLines={(v) => setM({ ...m, [pkKey]: v })} items={meta.raw_items} editable={mapEditable} cur={cur} prefix="pack" rateEdit={rateEdit} onSaveRate={perms.can_edit_map ? saveRate : null} />
            </div>
            <WastageBlock w={d.wastage} cur={cur} />
            <BasePricePanel key={`b${d.base_price}`} token={token} d={d} perms={perms} open={baseOpen} setOpen={setBaseOpen} onChanged={() => { load(editMap); onSaved(); }} />
            <PricePanel key={`c${d.price}`} token={token} d={d} center={center} perms={perms} open={centreOpen} setOpen={setCentreOpen} onChanged={() => { load(editMap); onSaved(); }} />
          </div>
        )}
        <DialogFooter className="gap-2 flex-wrap">
          <Button variant="ghost" size="sm" onClick={() => nav("/recipe-admin")} data-testid="open-recipe-master-btn">Open Recipe Master</Button>
          {perms.can_edit_map && <Button variant={editMap ? "default" : "outline"} size="sm" onClick={() => setEditMap(!editMap)} data-testid="edit-recipe-mapping-btn">Edit Recipe Mapping</Button>}
          {perms.can_edit_map && <Button variant={rateEdit ? "default" : "outline"} size="sm" onClick={() => setRateEdit(!rateEdit)} data-testid="update-rate-btn">Update Raw Material Rate</Button>}
          {perms.can_edit_price && <Button variant="outline" size="sm" onClick={() => setBaseOpen(!baseOpen)} data-testid="footer-edit-base-price-btn">Edit Base Price</Button>}
          {(perms.can_edit_price || perms.can_request) && <Button variant="outline" size="sm" onClick={() => setCentreOpen(!centreOpen)} data-testid="footer-edit-centre-price-btn">Edit Centre Price</Button>}
          <Button variant="outline" size="sm" onClick={() => { if (!editMap || window.confirm("Discard unsaved recipe-mapping edits and recalculate?")) { setEditMap(false); load(false); } }} data-testid="recalculate-btn">Recalculate</Button>
          {perms.can_edit_map && <Button size="sm" onClick={save} disabled={busy || !editMap} data-testid="costing-save-btn">Save Changes</Button>}
          <Button variant="ghost" size="sm" onClick={onClose} data-testid="costing-cancel-btn">Cancel</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
